import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { LocaleService } from '../locale/locale.service';
import { TranslationMapperService } from '../translation-mapper.service';
import { WINDOW_TOKEN } from '@usana/ux/common';
import { AppLinkDirective } from '../../../modules/common/app-link.directive';

@Component({
    selector: 'u-base',
    templateUrl: './base.component.html',
    styleUrls: ['./base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [AppLinkDirective],
})
export class BaseComponent implements OnInit, OnDestroy {
    @Input() src: any = '';
    @Input() position: any;
    el: ElementRef;
    hasRetailShopping = false;

    data;
    protected map;
    localeService;
    titleService;
    metaService;
    translationMapperService;
    dataIsLoaded = false;
    supportedLocale = true;
    syncLoadcontent = false;
    componentDataSubscription;
    localeSubscription;
    lazyLoad;
    window;

    constructor(
        protected http: HttpClient,
        localeService: LocaleService,
        titleService: Title,
        metaService: Meta,
        translationMapperService: TranslationMapperService,
        el: ElementRef,
    ) {
        this.localeService = localeService;
        this.titleService = titleService;
        this.metaService = metaService;
        this.translationMapperService = translationMapperService;
        this.el = el;
        this.window = inject(WINDOW_TOKEN);
    }

    ngOnInit(): void {
        this.lazyLoad = Array(6).fill(false);
        this.hasRetailShopping = this.localeService.getLocaleObject().hasRetailShopping;

        if (typeof this.src === 'string') {
            this.defaultComponentData();
            const loc: string = this.localeService.getLocale();
            if (this.src.trim().length > 0) {
                this.el.nativeElement.setAttribute('data-testid', this.src);
            }

            if (loc) {
                this.localeChangeBeforeLoadContent(loc);
                if (!this.syncLoadcontent) {
                    this.loadContent();
                    this.localeChangeAfterLoadContent(loc);
                }
            }
            this.localeSubscription = this.localeService.getLocaleObservable().subscribe((locale) => {
                this.localeChangeBeforeLoadContent(locale);
                this.loadContent();
                this.localeChangeAfterLoadContent(locale);
            });
        } else {
            this.data = this.src as any;
            this.dataIsLoaded = true;
        }
    }

    ngOnDestroy(): void {
        if (this.componentDataSubscription) {
            this.componentDataSubscription.unsubscribe();
        }
        if (this.localeSubscription) {
            this.localeSubscription.unsubscribe();
        }
    }

    hasInnerText(key) {
        if (key !== undefined || key !== null) {
            const elem = this.createElementFromHTML(key) as any;
            const len = elem.innerText.trim().length;
            return len > 0 ? true : false;
        } else {
            return false;
        }
    }

    createElementFromHTML(htmlString) {
        const div = document.createElement('div');
        if (htmlString !== undefined) {
            div.innerHTML = (htmlString as any) + ' '.trim();
        }
        return div;
    }

    protected localeChangeBeforeLoadContent(newLocale: string): void {}

    protected localeChangeAfterLoadContent(newLocale: string): void {}

    protected processComponentData(data): void {}

    protected defaultComponentData(): void {
        this.dataIsLoaded = false;
    }

    protected loadContent(): void {
        this.translationMapperService.translateFromMap(this.src, this.map).then((tData) => {
            this.data = tData;
            this.processComponentData(this.data);
            this.dataIsLoaded = true;
        });
    }

    public marketIn(list: string): boolean {
        return list.split(',').indexOf(this.localeService.getLocale()) >= 0;
    }

    getMaterial(itemNum: number) {
        const items = [];
        for (let i = 0; i < itemNum; i++) {
            items.push({
                imgUrl: 'items.' + i + '.imgUrl',
                imgUrlbig: 'items.' + i + '.imgUrlbig',
                desc: 'items.' + i + '.desc',
            });
        }
        return items;
    }

    captureView({ target, visible }: { target: Element; visible: boolean }): void {
        if (visible) {
            const promo = {
                id: target.attributes['data-id'].value,
                name: target.attributes['data-name'] ? target.attributes['data-name'].value : undefined,
                creative: target.attributes['data-creative'].value,
                position: `dotcom_${target.attributes['data-position'].value}`,
            };
            const promoViewArr = this.window.impressionBucket.filter((r) => r.event === 'EEpromotionView');
            const promos = promoViewArr[promoViewArr.length - 1].ecommerce.promoView.promotions;
            if (promos.length === 0 || promos.findIndex((x) => x.position === promo.position) === -1) {
                promos.push(promo);
            }
        }
    }

    captureClick({ currentTarget, visible }: { currentTarget: Element; visible: boolean }): void {
        const promo = {
            id: currentTarget.attributes['data-id'].value,
            name: currentTarget.attributes['data-name'].value,
            creative: currentTarget.attributes['data-creative'].value,
            position: `dotcom_${currentTarget.attributes['data-position'].value}`,
        };
        const obj = {
            event: 'EEpromotionClick',
            ecommerce: {
                promoClick: {
                    promotions: [promo],
                },
            },
        };
        this.window.dataLayer = this.window.dataLayer || [];
        this.window.dataLayer.push(obj);
    }

    urlPathId(url) {
        if (url !== null && url !== undefined) {
            const urlWithoutTrailingSlash = url.replace(/\/$/, '');
            const urlArr = urlWithoutTrailingSlash.split('/');
            return this.el.nativeElement.tagName + '~' + urlArr[urlArr.length - 1].split('?')[0];
        } else {
            return this.el.nativeElement.tagName + '~';
        }
    }
}
